import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import { openWindow } from '@change-corgi/core/window';

import { trackMovementPageShare } from 'src/app/pages/movement/shared/api';
import { MOVEMENT_EVENT_VIEW_CONTEXT } from 'src/app/pages/movement/shared/constants';
import { useCurrentUser } from 'src/app/shared/hooks/session';
import { shareFacebook } from 'src/app/shared/share/facebook';
import { shareTwitter } from 'src/app/shared/share/twitter';
import { shareWhatsapp } from 'src/app/shared/share/whatsapp';

import { useShareUrl } from './useSocialShares';

type VoidFunction = () => void;

const FB_TRACKING_EVENTS = {
	success: 'movement_share',
	intent: 'movement_share_intent',
	cancel: 'movement_share_cancel',
	failure: 'movement_share_failure',
};

const TWITTER_TRACKING_EVENTS = {
	success: 'movement_share',
};

const WHATSAPP_TRACKING_EVENTS = {
	success: 'movement_share',
};

const TRACKING_DATA = {
	context: MOVEMENT_EVENT_VIEW_CONTEXT,
	viewContext: MOVEMENT_EVENT_VIEW_CONTEXT,
};

// TODO migrate to using useAvailableShareAction()
// eslint-disable-next-line max-lines-per-function
export function useShareClickHandlers({ movementId, objective }: { movementId: string; objective: string }): {
	onEmailClick: VoidFunction;
	onFacebookClick: VoidFunction;
	onTwitterClick: VoidFunction;
	onWhatsAppClick: VoidFunction;
} {
	const currentUser = useCurrentUser();
	const utilityContext = useUtilityContext();
	const track = useTracking();
	const shareUrls = useShareUrl({ slug: movementId });
	const { translate } = useI18n();
	const userName = currentUser?.shortDisplayName ?? '';
	const countryCode = currentUser?.country?.countryCode;

	const onFacebookClick = useCallback(() => {
		void shareFacebook(
			shareUrls.facebook,
			{
				trackingData: { ...TRACKING_DATA, location: 'facebook', movement_id: movementId },
				trackingEvents: FB_TRACKING_EVENTS,
			},
			utilityContext,
			track,
		);
	}, [movementId, shareUrls, utilityContext, track]);

	const onTwitterClick = useCallback(
		() =>
			shareTwitter({
				url: shareUrls.twitter,
				comment: translate('corgi.movement.show.share.twitter', { objective }),
				options: {
					trackingData: { ...TRACKING_DATA, location: 'twitter', movement_id: movementId },
					trackingEvents: TWITTER_TRACKING_EVENTS,
					countryCode,
				},
				track,
				utilityContext,
			}),
		[countryCode, movementId, shareUrls, objective, translate, track, utilityContext],
	);

	const onEmailClick = useCallback(() => {
		const encodedSubject = encodeURIComponent(objective);
		const encodedBody = encodeURIComponent(
			translate('corgi.movement.show.share.email_body', {
				objective,
				url: shareUrls.email,
				from: userName,
			}),
		);
		const emailHref = `mailto:?body=${encodedBody}&subject=${encodedSubject}`;
		void trackMovementPageShare(
			{
				...TRACKING_DATA,
				movementId,
				location: 'email',
			},
			track,
		);
		openWindow(emailHref);
	}, [objective, translate, shareUrls.email, userName, movementId, track]);

	const onWhatsAppClick = useCallback(() => {
		void shareWhatsapp(
			objective,
			shareUrls.whatsapp,
			{
				trackingEvents: WHATSAPP_TRACKING_EVENTS,
				trackingData: { ...TRACKING_DATA, location: 'whatsapp', movement_id: movementId },
			},
			track,
		);
	}, [objective, shareUrls.whatsapp, movementId, track]);

	return {
		onEmailClick,
		onFacebookClick,
		onTwitterClick,
		onWhatsAppClick,
	};
}
